import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Rocket,
  Wand2,
  Settings,
  PlayCircle,
  CheckCircle2,
  Lightbulb,
  ChevronRight,
  Info,
  BookOpen,
  Check,
} from "lucide-react";
import { useUIStore, type OnboardingStepId } from "@/stores/ui.store";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function OnboardingModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    onboardingOpen,
    setOnboardingOpen,
    hasSeenOnboarding,
    markOnboardingSeen,
    completedOnboardingSteps,
    markOnboardingStepComplete,
  } = useUIStore();

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (!hasSeenOnboarding) {
      const timer = setTimeout(() => {
        setOnboardingOpen(true);
        markOnboardingSeen();
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [hasSeenOnboarding, setOnboardingOpen, markOnboardingSeen]);

  if (!mounted) return null;

  const visitStep = (id: OnboardingStepId, path: string) => {
    markOnboardingStepComplete(id);
    navigate(path);
    setOnboardingOpen(false);
  };

  const steps: {
    id: OnboardingStepId;
    icon: React.ReactNode;
    title: string;
    desc: string;
    tooltip: string;
    actionLabel: string;
    action: () => void;
  }[] = [
    {
      id: "rules",
      icon: <Wand2 className="h-5 w-5 text-primary" />,
      title: t("onboarding.step1Title"),
      desc: t("onboarding.step1Desc"),
      tooltip: t("onboarding.step1Tooltip"),
      actionLabel: t("onboarding.actionRules"),
      action: () => {
        visitStep("rules", "/rules");
      },
    },
    {
      id: "settings",
      icon: <Settings className="h-5 w-5 text-blue-500" />,
      title: t("onboarding.step2Title"),
      desc: t("onboarding.step2Desc"),
      tooltip: t("onboarding.step2Tooltip"),
      actionLabel: t("onboarding.actionSettings"),
      action: () => {
        visitStep("settings", "/settings");
      },
    },
    {
      id: "dashboard",
      icon: <PlayCircle className="h-5 w-5 text-success" />,
      title: t("onboarding.step3Title"),
      desc: t("onboarding.step3Desc"),
      tooltip: t("onboarding.step3Tooltip"),
      actionLabel: t("onboarding.actionDashboard"),
      action: () => {
        visitStep("dashboard", "/dashboard");
      },
    },
  ];

  const completedCount = steps.filter((s) => completedOnboardingSteps.includes(s.id)).length;

  const tips = [
    { title: t("onboarding.tip1Title"), desc: t("onboarding.tip1Desc") },
    { title: t("onboarding.tip2Title"), desc: t("onboarding.tip2Desc") },
    { title: t("onboarding.tip3Title"), desc: t("onboarding.tip3Desc") },
  ];

  return (
    <Dialog open={onboardingOpen} onOpenChange={setOnboardingOpen}>
      {/* Expanded to a wider dialog for a 2-column layout */}
      <DialogContent className="sm:max-w-3xl md:max-w-4xl overflow-hidden border-0 p-0 shadow-2xl">
        {/* Header Section with subtle gradient */}
        <div className="relative bg-gradient-to-r from-primary/10 via-background to-accent px-8 pb-6 pt-10">
          <div className="absolute -top-6 left-8 rounded-full bg-background p-2 shadow-sm ring-1 ring-border">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Rocket className="h-5 w-5 text-primary" />
            </div>
          </div>

          <DialogHeader className="text-left">
            <DialogTitle className="text-2xl font-extrabold tracking-tight text-foreground">
              {t("onboarding.title")}
            </DialogTitle>
            <DialogDescription className="mt-2 text-base text-muted-foreground max-w-2xl">
              {t("onboarding.description")}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* 2-Column Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
          {/* Left Column: Actionable Steps */}
          <div className="px-8 py-6 bg-background">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold text-foreground">{t("onboarding.setupGuideTitle")}</h3>
              </div>
              <span className="text-xs font-medium text-muted-foreground tabular-nums">
                {t("onboarding.progressLabel", { completed: completedCount, total: steps.length })}
              </span>
            </div>
            {/* Progress bar */}
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden mb-4">
              <div
                className="h-full rounded-full bg-success transition-all duration-500 ease-out"
                style={{ width: `${String((completedCount / steps.length) * 100)}%` }}
              />
            </div>

            <div className="flex flex-col space-y-4">
              <TooltipProvider>
                {steps.map((step) => {
                  const isComplete = completedOnboardingSteps.includes(step.id);
                  return (
                    <div
                      key={step.id}
                      className={cn(
                        "group relative flex flex-col gap-3 rounded-xl border p-4 transition-all",
                        isComplete
                          ? "border-success/30 bg-success/5"
                          : "border-border bg-card hover:border-primary/40 hover:shadow-md",
                      )}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-transform",
                              isComplete
                                ? "bg-success/10"
                                : "bg-muted group-hover:scale-110 group-hover:bg-primary/10",
                            )}
                          >
                            {step.icon}
                            {isComplete && (
                              <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-success ring-2 ring-background">
                                <Check className="h-2.5 w-2.5 text-success-foreground" />
                              </span>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-foreground">{step.title}</h4>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>{step.tooltip}</p>
                                </TooltipContent>
                              </Tooltip>
                              {isComplete && (
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-success">
                                  {t("onboarding.stepCompleted")}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                              {step.desc}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* Action Button inside step */}
                      <div className="flex justify-end">
                        <Button
                          variant={isComplete ? "ghost" : "secondary"}
                          size="sm"
                          className={cn(
                            "gap-1 text-xs transition-colors",
                            !isComplete && "hover:bg-primary hover:text-primary-foreground",
                          )}
                          onClick={step.action}
                        >
                          {isComplete ? t("onboarding.stepRevisit") : step.actionLabel}
                          <ChevronRight className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </TooltipProvider>
            </div>
          </div>

          {/* Right Column: Best Practices & Tips */}
          <div className="px-8 py-6 bg-accent/30">
            <div className="flex items-center gap-2 mb-6">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              <h3 className="font-semibold text-foreground">
                {t("onboarding.bestPracticesTitle")}
              </h3>
            </div>

            <div className="space-y-6">
              {tips.map((tip, idx) => (
                <div key={idx} className="flex gap-3">
                  <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-600">
                    <span className="text-xs font-bold">{idx + 1}</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{tip.title}</h4>
                    <p className="mt-1 text-sm text-muted-foreground">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional helpful block */}
            <div className="mt-8 rounded-lg bg-blue-500/10 p-4 border border-blue-500/20">
              <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-2">
                {t("onboarding.needMoreHelpTitle")}
              </h4>
              <p className="text-sm text-blue-600/80 dark:text-blue-400/80">
                {t("onboarding.needMoreHelpDesc")}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="bg-muted/50 px-8 py-4 sm:justify-end border-t border-border">
          <Button
            onClick={() => {
              setOnboardingOpen(false);
            }}
            className="gap-2 bg-primary shadow-sm transition-all hover:bg-primary/90 hover:shadow-md"
          >
            <CheckCircle2 className="h-4 w-4" />
            {completedCount === steps.length
              ? t("onboarding.buttonClose")
              : t("onboarding.buttonStart")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
